import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import TopBar from "components/common/TopBar";
import EventService from "service/event/EventService";
import AuthenticationService from "service/auth/AuthenticationService";
import AppointmentService from "modules/appointments/service/AppointmentService";
import useStatusChange from "modules/appointments/hooks/useStatusChange";
import PatientService from "modules/patient/service/PatientService";
import PrescriptionService from "modules/prescription/service/PrescriptionService";
import ChiefComplaintsService from "modules/patient/sections/chiefComplaints/ChiefComplaintsService";
import DiagnosisService from "modules/patient/sections/diagnosis/DiagnosisService";
import MedicineService from "modules/patient/sections/medicines/MedicineService";
import LabInvestigationService from "modules/patient/sections/labInvestigations/LabInvestigationService";
import AnthropometricService from "modules/patient/sections/anthropometric/AnthropometricService";
import {
  ANTH_V2_REFRESH,
  ANTH_V2_SELECT_PARAM,
} from "modules/patient/sections/anthropometric/events";
import TherapyService from "modules/patient/service/TherapyService";
import { normalizeLanguages, resolveAge, ageInMonths } from "modules/patient/helper/patientFormatters";
import ReferenceListService from "service/network/ReferenceListService";
import LabTestService from "modules/prescription/service/LabTestService";
import {
  complaintsToGroups,
  diagnosisToGroups,
  medicinesToGroups,
  labInvestigationsToGroups,
  unresolvedUnitRows,
  applyUnitRefs,
  medicineGaps,
  myActorIds,
  myUid,
  touchedThisConsultation,
  toDoctorDetails,
  buildSaveBody,
  HEIGHT_UOM_CM,
} from "../helper/rxMappers";
import { signatureFor } from "../helper/doctorSignatures";
import { startRxSession, logRxEvent, rxPublished } from "../helper/rxPublishEvents";
import cleverTapAB from "util/cleverTapAB";
import PatientHeaderCard from "modules/patient/components/PatientHeaderCard";
import PatientDetailsDialog from "modules/patient/components/PatientDetailsDialog";
import {
} from "modules/appointments/helper/appointmentConstants";
// This screen reuses the patient page frame (.patient-page/.patient-scroll,
// .patient-message) and the header card's .ph-btn — import their stylesheet
// explicitly rather than relying on PatientDetails happening to be loaded.
import "modules/patient/styles/patient.scss";

import RxPickerCard from "./RxPickerCard";
import RxSectionTabs, { useSectionSpy } from "./RxSectionTabs";
import PatientDetailsCard from "./PatientDetailsCard";
import CustomInstructionCard, { instructionLines, toInstructionEntries } from "./CustomInstructionCard";
import AdvisedInvestigationsCard from "./AdvisedInvestigationsCard";
import RemoveMedicineDialog from "./RemoveMedicineDialog";
import ReferralsCard from "./ReferralsCard";
import FollowUpCard from "./FollowUpCard";
import PrescriptionDocument from "./PrescriptionDocument";
import {
  PICKERS,
  PATIENT_FIELDS,
  INITIAL_PICKS,
  INITIAL_REFERRALS,
  INITIAL_FOLLOW_UP,
  buildDocument,
} from "../helper/rxData";
import {
  saveScratch,
  readScratch,
  clearScratch,
  mergePicks,
  declinedDefaults,
  restoredDeclined,
  stampsFor,
} from "../helper/rxScratch";
import { overrideMemberId, scratchKeyFor } from "modules/patient/helper/bookingFor";
import useOriginalBookingName from "modules/patient/components/useOriginalBookingName";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobilePrescriptionScreen from "mobile/screens/MobilePrescriptionScreen";
import "../styles/prescription.scss";

/**
 * Review prescription — Figma "Screen / review prescription" (1762:15386).
 * Reached from the patient screen's "Create Rx"; the route mirrors Bridge's
 * /prescription/{id}, keyed on the appointment id like the patient screen.
 *
 * Two equal 728px columns inside the 1528 Content Host: the builder on the left,
 * a LIVE document preview on the right. This component owns all builder state so
 * the preview is a pure derivation of it (see buildDocument) — the two panes
 * cannot drift apart.
 *
 * UI-only: the clinical lists are hardcoded sample data and Publish Rx is inert.
 */
/**
 * "Send this prescription to the patient via…" — bridge's publish dialog. The
 * switches travel as QUERY params on the save; the backend does the sending.
 */
const PublishDialog = ({ patient, saving, onClose, onPublish }) => {
  const [email, setEmail] = useState(Boolean(patient.email));
  const [emailId, setEmailId] = useState(patient.email || "");
  const [sms, setSms] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const emailValid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailId.trim());

  return ReactDOM.createPortal(
    <div className="rx-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="rx-modal" role="dialog" aria-modal="true" aria-label="Publish prescription">
        <h2 className="rx-modal-title">Publish prescription</h2>
        <p className="rx-modal-sub">Send it to the patient via:</p>

        <label className="rx-check">
          <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
          Email
        </label>
        {email && (
          <input
            className="rx-modal-input"
            type="email"
            value={emailId}
            placeholder="name@example.com"
            onChange={(e) => setEmailId(e.target.value)}
          />
        )}
        <label className="rx-check">
          <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} />
          SMS
        </label>
        <label className="rx-check">
          <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />
          WhatsApp
        </label>

        {!emailValid && <p className="rx-note is-error">Enter a valid email address.</p>}

        <div className="rx-modal-actions">
          <button type="button" className="ph-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="ph-btn ph-btn-primary"
            disabled={saving || !emailValid}
            onClick={() =>
              onPublish({ email: email ? emailId.trim() : "", sms, whatsapp })
            }
          >
            {saving ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
PublishDialog.propTypes = {
  patient: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
};

/* A referral counts toward the prescription only while it is ticked. Unticked rows
   stay on the card (so they can be re-selected) but must reach neither the document
   nor the wire. `!== false` keeps referrals restored from older drafts, which have no
   `selected` flag, counting as ticked. */
const pickedReferrals = (list) => (list || []).filter((r) => r.selected !== false);

/* Mirrors $rx-pane-inset in prescription.scss: the offset a pinned pane parks at.
   If that changes, change this — the pane is considered pinned at this top. */
const RX_PANE_INSET = 16;

/**
 * `(card, rowId) => marker` over a pickers list: a row's `stamp` (any change to
 * its record) or `myStamp` (the doctor's own changes) — see rxScratch.
 */
const stampIn = (pickers, field = "stamp") => (key, id) => {
  const card = (pickers || []).find((p) => p.key === key);
  const row =
    card && card.groups.reduce((all, g) => all.concat(g.rows), []).find((r) => r.id === id);
  return (row && row[field]) || "";
};

const ReviewPrescription = ({ match, history }) => {
  const appointmentId = Number(match.params.appointmentId);

  const appointmentService = useRef(new AppointmentService()).current;
  const patientService = useRef(new PatientService()).current;
  const rxService = useRef(new PrescriptionService()).current;
  const ccService = useRef(new ChiefComplaintsService()).current;
  const dxService = useRef(new DiagnosisService()).current;
  const mdService = useRef(new MedicineService()).current;
  const liService = useRef(new LabInvestigationService()).current;
  const ltService = useRef(new LabTestService()).current;
  const refService = useRef(new ReferenceListService()).current;
  const auth = useRef(new AuthenticationService()).current;

  /* MOBILE ONLY. Everything mobile lives under src/mobile/; this line, the
     branch in goAddIn below, and the branch at the end of render are its whole
     footprint here. Called unconditionally, as a hook must be. */
  const mobile = useIsMobile() && MOBILE.prescriptionScreen;

  const [appointment, setAppointment] = useState(null);
  const originalBookingName = useOriginalBookingName(appointment);
  const [patient, setPatient] = useState(null);
  /* The header's preferred-languages line. This screen used to hand the header a
     hardcoded `languages={[]}`, which is the same mistake the therapy chip below
     had: the props were never passed, so the control had nothing to show.

     It was not merely cosmetic. An empty list makes the line read
     "+ Add preferred languages" on a patient who already has them, and the
     popover seeds its working set from what it is given — so adding one here
     sent a ONE-LANGUAGE list as the full replacement. Harmless only while the
     backend ignored the payload; now that it applies it, that would have
     overwritten the patient's real languages from the prescription screen. */
  const [languages, setLanguages] = useState([]);
  const [languagesState, setLanguagesState] = useState("loading");
  const [reasons, setReasons] = useState([]);
  // The header's past/upcoming strip, same rule as the patient screen: split on
  // THIS appointment's date. Decoration — a failure leaves the empty states.
  /* The header's therapy chip. Without these the chip rendered its empty state
     ("Set therapy") on a patient who has one — the props were simply never
     passed, so it had nothing to show. */
  const [condition, setCondition] = useState("");
  const [therapyTag, setTherapyTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ---------- builder state: the single source the preview derives from ---------- */
  const [fields, setFields] = useState(PATIENT_FIELDS);
  /* Set the moment the prescriber edits Weight, so the anthropometric prefill
     (which lands asynchronously) can never overwrite what they typed. */
  const weightTouched = useRef(false);
  const [picks, setPicks] = useState(INITIAL_PICKS);
  // [{ id, lines }] — one entry per instruction box (CustomInstructionCard).
  const [customInstructions, setCustomInstructions] = useState([]);
  /** Parked boxes that were typed but never added, handed back to the card. */
  const [restoredInstructionDrafts, setRestoredInstructionDrafts] = useState(null);
  // Both are now SENT as well as previewed: referrals ride on the prescription's
  // optional `referrals`, and the follow-up is stored twice — as the instruction
  // sentence that prints, and as the structured `followUp` (count/unit/days/date)
  // so it can actually be queried.
  const [referrals, setReferrals] = useState(INITIAL_REFERRALS);
  const [followUp, setFollowUp] = useState(INITIAL_FOLLOW_UP);
  /**
   * Advised investigations — `[{ id, labTest: {label, value}, instruction }]`.
   *
   * NOT part of `picks`: the other four cards choose from the patient's record,
   * this one CREATES what is being advised, so there is nothing to tick and no
   * seed to fetch. It starts empty on every fresh Rx, exactly like bridge's.
   */
  const [investigations, setInvestigations] = useState([]);
  /**
   * The rows that were ticked FOR the doctor rather than BY them — captured in
   * `done()`, which is the only place that can tell the two apart. They can't
   * be un-ticked (Figma 3031:9675): a consultation medicine leaves the
   * prescription through its × and the confirm dialog, not through the box.
   *
   * NOT derived from `row.isNew`: on the saved-draft path every row arrives
   * ticked while `isNew` is computed separately, so the two diverge there.
   *
   * Medicine ids only — no other card locks — and always a subset of `picks`.
   */
  const [lockedPicks, setLockedPicks] = useState({});
  /* Every card's DEFAULT ticks on this visit — what done() ticked for the
     doctor. Parked with the scratch so the next visit can tell a default they
     took off (declined) from a row they never had. */
  const [autoPicks, setAutoPicks] = useState({});
  // The medicine row whose × was clicked; null when the dialog is closed.
  const [removingMedicine, setRemovingMedicine] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  /* ---------- live Rx data (null until loaded; falls back to the sample deck) ---------- */
  const [pickers, setPickers] = useState(null);
  const [rxLoading, setRxLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  // The publish dialog's "send this prescription to the patient via…" switches.
  const [notifyOpen, setNotifyOpen] = useState(false);

  /* ---------- "jump to section" tabs (Figma 3032:1253) ---------- */
  const SECTION_TABS = [
    { id: "rx-sec-patient", label: "Patient details" },
    /* Chief complaints BEFORE diagnosis (product, 2026-09-10) — the complaint
       is what the patient reported and the diagnosis is what was concluded
       from it, so the record reads in that order. Four places express this
       order and all four move together: this strip, the live picker list in
       done(), the PICKERS fallback (rxData) and the document's lavender band
       (PrescriptionDocument SUMMARY_LINES). The generated PDF's own band is a
       fifth, in api-prescription-service. */
    { id: "rx-sec-complaints", label: "Chief complaints" },
    { id: "rx-sec-diagnosis", label: "Diagnosis" },
    { id: "rx-sec-reports", label: "Lab investigations" },
    { id: "rx-sec-medicines", label: "Medicine" },
    { id: "rx-sec-investigations", label: "Advised investigations" },
    { id: "rx-sec-instruction", label: "Custom instruction" },
    { id: "rx-sec-referrals", label: "Referrals" },
    { id: "rx-sec-followup", label: "Follow-up" },
  ];
  const TAB_FOR_PICKER = {
    diagnosis: "rx-sec-diagnosis",
    complaints: "rx-sec-complaints",
    reports: "rx-sec-reports",
    medicines: "rx-sec-medicines",
  };

  /**
   * The header "+ Add …" buttons. Labels are Figma's, verbatim and irregular —
   * "Add complaint" is singular while "Add investigations" is plural — so they
   * are listed rather than generated from the card title (which would produce
   * "Add chief complaints" and, worse, disagree with the design where the code
   * and Figma titles differ).
   *
   * `investigations` is absent DELIBERATELY: that card is Figma's "Advised
   * investigations", where the design uses a search field, not an add button —
   * and there is no patient-record section for tests you are ordering.
   */
  const ADD_LABEL_FOR_PICKER = {
    diagnosis: "Add diagnosis",
    complaints: "Add complaint",
    reports: "Add lab investigation",
    medicines: "Add medicine",
  };

  /** Where each card's "+ Add" lands in the patient record's section tabs. */
  const PATIENT_SECTION_FOR_PICKER = {
    diagnosis: "diagnosis",
    complaints: "chief-complaints",
    reports: "lab-investigations",
    medicines: "medicines",
  };

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* ---------- surviving the trip to the patient record ----------
   *
   * "+ Add diagnosis" takes the doctor off this screen, and the builder holds
   * work that lives nowhere else. It is parked in sessionStorage on the way out
   * and picked back up on the way in — LOCAL ONLY (Nittin, 2026-07-27): a
   * detour must not mint a server draft for an Rx that may never be written.
   *
   * Read once, as soon as the appointment is known — before the patient record
   * lands, so the seed can merge against it. Not at mount: while "Update
   * booking for" has switched the appointment, the scratch is parked per
   * MEMBER, so a draft started for one person is never restored onto another's
   * prescription.
   */
  const restoredRef = useRef(null);
  const scratchKeyRef = useRef(null);
  // Refs so the unmount handler sees the LAST state, not the first render's.
  // `declined` is derived here, from the rows on screen, so what is parked is
  // always this render's.
  const latest = useRef({});
  latest.current = {
    fields,
    picks,
    customInstructions,
    referrals,
    followUp,
    investigations,
    autoPicks,
    autoStamps: stampsFor(autoPicks, stampIn(pickers)),
    declined: declinedDefaults(
      restoredDeclined(restoredRef.current, true),
      autoPicks,
      picks,
      stampIn(pickers, "myStamp")
    ),
  };
  // Flipped off once the prescription is saved — a saved Rx is the record, and
  // re-restoring stale edits over it would silently resurrect them.
  const keepScratch = useRef(true);
  /* Set once the seed has APPLIED the parked scratch (or found none). Until
     then the builder's state is still the empty initial one, and saving it —
     which clears the parked copy when there is nothing worth keeping — would
     destroy work the doctor has not even seen restored yet, if they leave
     before the lists load. */
  const scratchSettled = useRef(false);

  /* Which builder parked the scratch — see the publish success handler. */
  const scratchOwner = useRef(`rx-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  useEffect(
    () => () => {
      if (keepScratch.current && scratchSettled.current && scratchKeyRef.current) {
        saveScratch(
          scratchKeyRef.current,
          { ...latest.current, customInstructionDrafts: instructionDrafts.current },
          scratchOwner.current
        );
      }
    },
    [appointmentId]
  );

  /**
   * The logged-in employee — the Rx payload's createdBy/updatedBy.
   *
   * `uid` (the JWT's platform auth id) is what a stored prescription actually
   * records, and it is the same id the v2 services stamp into
   * `reportedBy.whoId`. `id` stays as the fallback: on the MFA login path the
   * stored profile may carry no top-level id at all.
   */
  const employee = (() => {
    const u = auth.getUserProfile() || {};
    const e = u.employeeProfile || {};
    return {
      uid: myUid(u),
      id: u.id || e.id,
      // Collapse whitespace, don't just trim the ends — a stored firstName of
      // "Tara " was going out as "Tara  Emp" with a double space.
      /* employeeProfile is null on the doctor login, and this name is what
         toDoctorDetails prints on the prescription — fall back rather than
         sign it blank. */
      name:
        [e.firstName, e.lastName]
          .filter(Boolean)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim() ||
        (u.doctorProfile && u.doctorProfile.name) ||
        "",
    };
  })();

  /**
   * The prescriber is ALWAYS the logged-in doctor — a prescription can't be
   * written on another doctor's behalf (Nittin's rule, same as Create
   * appointment's self-only provider guard). So there is no doctor picker: we
   * take the signed-in doctorProfile and, when the patient's care team lists
   * that same doctor, enrich it with the licence/speciality held there.
   *
   * No doctorProfile.id (a pure ops employee) => no prescriber => publishing is
   * blocked. Fail closed rather than publish an unsigned prescription.
   */
  const myDoctor = (() => {
    const u = auth.getUserProfile() || {};
    const dp = u.doctorProfile || {};
    if (!dp.id) return null;
    // doctorProfile IS the whole doc_profile row, so it — not the care team —
    // is the primary source. The care-team copy is only a fallback: it rides on
    // an optional `careTeam` key whose upstream fetch fails silently, and
    // spreading it first used to strip clinic/location/title/etc. off a
    // published Rx whenever that fetch was down.
    const fromTeam = doctors.find((d) => String(d.id) === String(dp.id)) || {};
    return toDoctorDetails(dp, fromTeam, employee.name);
  })();

  const goToDashboard = useCallback(() => history.push("/navigo-module"), [history]);
  const goToPatient = useCallback(
    () => history.push(`/navigo-module/patient/${appointmentId}`),
    [history, appointmentId]
  );

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  /* ---------- appointment, then the patient it points at ---------- */
  useEffect(() => {
    let cancelled = false;

    if (!appointmentId) {
      setError("That prescription link looks wrong.");
      setLoading(false);
      return undefined;
    }

    appointmentService.fetchAppointmentById(
      appointmentId,
      (res) => {
        if (cancelled) return;
        const found = res && res.appointments && res.appointments[0];
        if (!found) {
          setError("We couldn't find that appointment.");
          setLoading(false);
          return;
        }
        setAppointment(found);

        /* WHO THE PRESCRIPTION IS FOR. On a family booking the visit — and so
           the prescription — is for `bookedFor`, the member; `patientID` is
           only the account holder who booked and paid. The server says the same
           thing, overwriting the row's patientName/patientGender/patientEmail
           with the member's whenever `BookedFor != 0`
           (api-appointment-service repository/appointment.go:911-916).

           This screen used to load `patientID` unconditionally, so a
           prescription written for a child was addressed to, measured against
           and SAVED under the parent — while the patient record screen (which
           resolves this correctly) showed the child. Same resolution as that
           screen and as the dashboard's Patient ID column. */
        /* ...unless phr's booking-for mapping (the appointment's `updatedBookingFor`,
           "Update booking for") points it at another member — then THAT member. */
        const pid =
          overrideMemberId(found) ||
          (found.bookedFor && found.bookedFor !== 0 ? found.bookedFor : found.patientID);

        // The parked Rx scratch for THIS member (see restoredRef above).
        scratchKeyRef.current = scratchKeyFor(appointmentId, overrideMemberId(found));
        restoredRef.current = readScratch(scratchKeyRef.current);

        patientService.fetchPatient(
          pid,
          (p) => {
            if (cancelled) return;
            if (!p || !p.id) {
              setError("We couldn't load this patient's record.");
            } else {
              setPatient(p);
              /* Alongside the record, not after it: the header renders as soon
                 as `patient` lands, and "loading" is what keeps the line from
                 claiming anything until this answers. */
              patientService.fetchPatientLanguages(
                pid,
                (langs) => {
                  if (cancelled) return;
                  setLanguages(normalizeLanguages(langs));
                  setLanguagesState("ok");
                },
                (err) => {
                  if (cancelled) return;
                  setLanguages([]);
                  setLanguagesState("error");
                  // eslint-disable-next-line no-console
                  console.warn("preferred languages: fetch failed", err);
                }
              );
              // Seed the editable details from the real record, falling back to
              // the sample copy where the record is thin.
              setFields((prev) => ({
                ...prev,
                fullName: [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || prev.fullName,
                /* CALCULATED, not copied. `p.age` is whatever was stored when
                   the record was last written, so it goes stale as the patient
                   ages; resolveAge derives it from dateOfBirth and only falls
                   back to the stored number when there is no DOB. The field is
                   read-only in the card for the same reason. */
                age: String(resolveAge(p.dateOfBirth, p.age) || ""),
                /* UNDER A YEAR THE AGE IS MONTHS (Nittin, 2026-09-24). resolveAge
                   answers 0 there, which this used to render as an empty box and
                   print on the prescription as "/F". `age` stays the number of
                   WHOLE YEARS — the wire's field is an int and six months is not
                   six years — and the months ride beside it. */
                ageMonths: String(ageInMonths(p.dateOfBirth) || ""),
                gender: p.gender === 1 ? "Female" : p.gender === 2 ? "Male" : prev.gender,
                phone: [p.countryCode ? `+${p.countryCode}` : "", p.phoneno]
                  .filter(Boolean)
                  .join("-") || prev.phone,
              }));
            }
            setLoading(false);
          },
          (err) => {
            if (cancelled) return;
            setError(`We couldn't load this patient's record. ${(err && err.message) || ""}`);
            setLoading(false);
          }
        );
      },
      (err) => {
        if (cancelled) return;
        setError(`We couldn't load that appointment. ${(err && err.message) || ""}`);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [appointmentId, appointmentService, patientService]);

  /* Re-read the record after the header's Patient-details dialog saves.
     WITHOUT THIS THE DIALOG LOOKS BROKEN: the shared header only refreshes if
     its owner hands it `onPatientUpdated` (PatientHeaderCard.js:653-656), the
     patient screen does and this screen did not — so the PUT succeeded, the
     dialog closed, and nothing on the page moved. Reopening it then showed the
     pre-edit record with "Save changes" grey again, which is what was reported
     as editing not working from Create Rx (Nittin, 2026-09-12).

     `fields` is re-seeded as well as `patient`: the document prints from
     `fields`, so refreshing only the header would have left the prescription
     itself carrying the old name, age, gender and phone. Same four lines as
     the initial load.

     Re-seeded from the OPTIMISTIC copy too, not only from the server's (bridge's
     rule): the preview prints name and age from `fields` while the save body
     takes gender from `patient` (rxMappers buildSaveBody), so a failed re-read
     used to leave a published Rx with the old name beside the new gender. A
     sequence number drops an older response that lands after a newer one. */
  const identityFields = (p, prev) => ({
    ...prev,
    fullName: [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || prev.fullName,
    age: String(resolveAge(p.dateOfBirth, p.age) || ""),
    /* UNDER A YEAR THE AGE IS MONTHS (Nittin, 2026-09-24). resolveAge
       answers 0 there, which this used to render as an empty box and
       print on the prescription as "/F". `age` stays the number of
       WHOLE YEARS — the wire's field is an int and six months is not
       six years — and the months ride beside it. */
    ageMonths: String(ageInMonths(p.dateOfBirth) || ""),
    gender: p.gender === 1 ? "Female" : p.gender === 2 ? "Male" : prev.gender,
    phone: [p.countryCode ? `+${p.countryCode}` : "", p.phoneno]
      .filter(Boolean)
      .join("-") || prev.phone,
  });
  const patientIdRef = useRef(null);
  patientIdRef.current = patient && patient.id;
  const refetchSeq = useRef(0);
  const refetchPatient = useCallback(
    (optimistic) => {
      /* The screen's OWN record id, never the caller's: an optimistic copy of
         some other member must not repaint this prescription. */
      const id = patientIdRef.current;
      if (!id) return;
      const mine = ++refetchSeq.current;
      if (optimistic && String(optimistic.id) === String(id)) {
        const o = {
          ...optimistic,
          firstName: String(optimistic.firstName || "").trim(),
          lastName: String(optimistic.lastName || "").trim(),
          email: String(optimistic.email || "").trim(),
        };
        setPatient(o);
        setFields((prev) => identityFields(o, prev));
      }
      patientService.fetchPatient(
        id,
        (p) => {
          if (!mounted.current || mine !== refetchSeq.current) return;
          if (!p || !p.id || String(p.id) !== String(id)) return;
          // React 16 does not batch inside async callbacks: without this the
          // header would paint the new record one frame before the document.
          ReactDOM.unstable_batchedUpdates(() => {
            setPatient(p);
            setFields((prev) => identityFields(p, prev));
          });
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.warn("patient record: refetch after save failed", err);
        }
      );
    },
    // identityFields is a pure local helper; patientService is a stable ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientService]
  );

  /* The legacy Rx screen's events (helper/rxPublishEvents): one session per
     patient this screen opens, which logs START_PRESCRIPTION and reads the
     plan RX_PUBLISH reports. Bridge's CleverTap A/B account starts here as
     well, in every environment as bridge's own does (the TEST account off
     production): the SDK holds events until its server has handed it a
     session, so it must be up well before a publish. */
  const rxSession = useRef(null);
  const sessionPatientId = patient && patient.id;
  useEffect(() => {
    if (!sessionPatientId) return;
    try {
      cleverTapAB();
    } catch (e) {
      // Analytics only: never let it take the screen down.
      // eslint-disable-next-line no-console
      console.warn("rx events: starting CleverTap failed", e);
    }
    rxSession.current = startRxSession(rxService, {
      app: "phr-frontend",
      userId: (auth.getUserProfile() || {}).id,
      patientId: sessionPatientId,
    });
  }, [sessionPatientId, rxService, auth]);

  /* The Patient-details dialog, owned HERE: the header's name and the Patient
     details card's "Update" both open this one mount (the header takes
     `onEditDetails`). Mounted outside .rx-host, not inside the card: the panes
     are position:sticky, a stacking context that would trap a fixed scrim
     under the Document preview pane. */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const openDetails = useCallback(() => setDetailsOpen(true), []);

  /* Publish refuses while the Custom instruction card holds a box that would
     NOT print as shown — a new box not yet added, or one over the limit —
     rather than publish a prescription that silently leaves it out. */
  /* Assigned below, where the section spy is set up — requestPublish is
     declared before it. */
  const jumpRef = useRef(null);
  const instructionsPending = useRef({ unadded: 0, tooLong: 0 });
  /* Instruction boxes typed but not added. Parked with the rest of the builder
     so a "+ Add …" detour cannot destroy them; read at unmount, so a ref. */
  const instructionDrafts = useRef([]);
  const onInstructionDrafts = useCallback((d) => {
    instructionDrafts.current = d || [];
  }, []);
  const onInstructionsPending = useCallback((p) => {
    instructionsPending.current = p || { unadded: 0, tooLong: 0 };
  }, []);
  const requestPublish = useCallback(() => {
    const p = instructionsPending.current;
    if (p.unadded || p.tooLong) {
      showError(
        p.tooLong
          ? "A custom instruction is over 500 characters. Shorten it before publishing."
          : "A custom instruction hasn't been added to the prescription. Add it or remove it before publishing."
      );
      /* The SECTION TABS' own jump, not a centred scrollIntoView: centring
         lands 10px short of the inset the panes pin at, so the left pane had
         scrolled its own overflow while the page had not pinned — the cards
         above the instruction card were then unreachable until the doctor
         scrolled DOWN first. `jumpTo` sets the head offset and scrolls to the
         card's start, which pins. */
      if (jumpRef.current) jumpRef.current("rx-sec-instruction");
      return;
    }
    setNotifyOpen(true);
  }, [showError]);

  /* Re-read after the header's popover saves, so the line reflects the new set
     without reloading the prescription screen — the same contract
     PatientDetails has with that popover. */
  const refetchLanguages = useCallback(() => {
    if (!patient || !patient.id) return;
    patientService.fetchPatientLanguages(
      patient.id,
      (res) => {
        setLanguages(normalizeLanguages(res));
        setLanguagesState("ok");
      },
      (err) => {
        setLanguages([]);
        setLanguagesState("error");
        // eslint-disable-next-line no-console
        console.warn("preferred languages: refetch failed", err);
      }
    );
  }, [patient, patientService]);

  /* ---------- seed the prescription from the patient's own records ----------
   *
   * There is NO draft (product decision, 2026-07-29): the screen always builds
   * fresh from the patient's records, and Publish is the only way out. Nothing
   * is read back from a stored prescription, so work in progress lives only in
   * the local scratch (rxScratch) until it is published.
   *
   * Seeds (each independent; a failure just leaves that card empty rather than
   * blocking the screen): complaints from phr's own CC v2 service, diagnosis +
   * reports + investigations from the Rx seed endpoints, ongoing medicines from
   * the v2 tracking snapshot (which also carries the optimistic-lock version).
   */
  /* KEYED ON THE MEMBER, not on the record object.
  
     Everything below is per-patient — the seed, the parked scratch, the care
     team — and re-running it because the SAME patient's record was re-read
     would be worse than wasteful. `applyRestored()` at the end of it replays
     the scratch parked on the LAST visit (saveScratch only runs on unmount),
     so a mid-session re-seed would put the previous visit's referrals,
     instructions and follow-up back over everything the doctor has typed
     since, and re-fetch every picker to do it.
  
     That became reachable the moment the Patient-details dialog started
     re-reading the record after it saves (`refetchPatient` above): a new
     record object for the same member. The id is what this effect is actually
     about — it only ever read `patient.id` and the care team — so it keys on
     the id, and the care team comes through a ref, which is stable and needs
     no dep.

     THE APPOINTMENT THE SAME WAY. The effect used to depend on the appointment
     OBJECT, and the header's status control applies a change with
     `setAppointment((prev) => ({ ...prev, status }))` — a new object for the
     same appointment — so "Call later" mid-prescription re-ran all of the
     above: every tick back to its default, the arrival scratch replayed, every
     list re-fetched. The seed reads exactly one thing from the appointment, its
     date (which rows count as added in this consultation), so it keys on that
     and the id, and a status-only change leaves the builder alone. */
  const careTeamRef = useRef([]);
  careTeamRef.current = (patient && patient.careTeam && patient.careTeam.doctors) || [];
  const seedPatientId = patient && patient.id;
  const seedAppointmentId = appointment && appointment.id;
  const seedConsultDate = (appointment && appointment.appointmentDate) || null;
  useEffect(() => {
    if (!seedPatientId) return undefined;
    let cancelled = false;
    const pid = seedPatientId;
    const restored = restoredRef.current;
    setRxLoading(true);

    // The patient record carries the care team — that IS the doctor dropdown.
    setDoctors(careTeamRef.current);

    /**
     * Work parked on the way to the patient record, put back LAST — after the
     * draft and the patient record have had their say, because what the doctor
     * typed is the newer fact.
     */
    const applyRestored = () => {
      if (!restored) return;
      const kept = restored.fields || {};
      setFields((prev) =>
        Object.keys(kept).reduce(
          (next, k) => {
            if (kept[k] !== "" && kept[k] != null) next[k] = kept[k];
            return next;
          },
          { ...prev }
        )
      );
      /* MERGED, not replaced: this lands after the builder's fetches, while the
         instruction card is already live, so a box added in that window must
         survive. Parked plain strings (the old shape) become one-line entries. */
      if ((restored.customInstructionDrafts || []).length) {
        setRestoredInstructionDrafts(restored.customInstructionDrafts);
      }
      const parkedInstructions = toInstructionEntries(restored.customInstructions);
      if (parkedInstructions.length) {
        setCustomInstructions((prev) => {
          const seen = {};
          parkedInstructions.forEach((e) => {
            seen[e.id] = true;
          });
          return parkedInstructions.concat(prev.filter((e) => !seen[e.id]));
        });
      }
      if (restored.referrals) setReferrals(restored.referrals);
      if (restored.followUp) setFollowUp(restored.followUp);
      if (restored.investigations) setInvestigations(restored.investigations);
    };

    const consultDate = seedConsultDate;
    /** `basePicks` is the draft's own selection; seeding derives it from isNew. */
    const done = (groups, basePicks) => {
      if (cancelled) return;
      setPickers([
        { key: "complaints", title: "Chief complaints", groups: groups.complaints || [] },
        { key: "diagnosis", title: "Diagnosis", groups: groups.diagnosis || [] },
        { key: "reports", title: "Lab investigations", groups: groups.reports || [] },
        { key: "medicines", title: "Medicine", groups: groups.medicines || [] },
      ]);
      // Only what THIS consultation added or updated is ticked by default
      // (Nittin, 2026-07-27; updates 2026-09-14): those rows carry `isNew` or
      // `isUpdated` — added, or saved, today by the logged-in user. Ongoing
      // rows stay unticked for the doctor to choose.
      const rowsOf = (k) => (groups[k] || []).reduce((all, g) => all.concat(g.rows), []);
      const fresh = {};
      Object.keys(groups).forEach((k) => {
        fresh[k] = rowsOf(k)
          .filter(touchedThisConsultation)
          .map((r) => r.id);
      });
      const base = basePicks || fresh;
      // Coming back from "+ Add diagnosis": keep what was already ticked AND
      // tick what the detour just added (it is a default by definition). Union,
      // not replace — either alone loses half of what the doctor did. Two
      // exceptions, both in mergePicks: a default the doctor took off stays off
      // until that row changes, and a tick that was only a default goes once the
      // row stops being one (a colleague re-dosed "my" medicine meanwhile).
      const seeded = [
        { key: "complaints", groups: groups.complaints || [] },
        { key: "diagnosis", groups: groups.diagnosis || [] },
        { key: "reports", groups: groups.reports || [] },
        { key: "medicines", groups: groups.medicines || [] },
      ];
      const start = restored
        ? mergePicks(restored.picks, base, {
            autoPicks: restored.autoPicks,
            autoStamps: restored.autoStamps,
            declined: restoredDeclined(restored, true),
            stampOf: stampIn(seeded),
            declineStampOf: stampIn(seeded, "myStamp"),
          })
        : base;
      setPicks(start);
      setAutoPicks(base);
      // Locked = the medicine defaults — an updated medicine as much as a new
      // one (Nittin, 2026-09-14) — EXCEPT an updated one the prescription can't
      // carry (`gap`, e.g. no formulation on record): the save guard tells the
      // doctor to untick it, and a lock would leave deleting a long-standing
      // medicine from the patient's record as the only way past it. Recomputed
      // (not merged) on every entry, and never a row that isn't ticked.
      setLockedPicks({
        medicines: rowsOf("medicines")
          .filter((r) => (base.medicines || []).indexOf(r.id) !== -1 && !(r.isUpdated && r.gap))
          .map((r) => r.id)
          .filter((id) => (start.medicines || []).indexOf(id) !== -1),
      });
      applyRestored();
      scratchSettled.current = true;
      setRxLoading(false);
    };

    /**
     * Seed a fresh Rx from PHR's OWN APIs — bridge's tracking endpoints are
     * deliberately NOT used here (Nittin, 2026-07-27).
     *
     * Live: chief complaints (CC v2), diagnosis (Diagnosis v2) and medicines
     * (Medicine v2), all three of phr's own section services. Reports and lab
     * investigations stay empty until phr's endpoints exist; each is a one-line
     * swap at the marked seam below. An empty card is the honest state — better
     * than showing another system's data the rest of phr can't edit.
     */
    // Prefill height / weight / BMI / weigh-in date. Runs on BOTH paths (draft
    // and fresh) but never clobbers a value the draft already carried — and
    // dateOfWeightRecorded is required by the save, so this is load-bearing.
    rxService.getPersonalDetails(
      pid,
      employee.id,
      employee.name,
      (res) => {
        if (cancelled || !res || !res.PersonalDetails) return;
        const pd = res.PersonalDetails;
        // The weigh-in date arrives split across three fields, not as a date.
        const weighed =
          pd.yearWeightRecorded && pd.monthWeightRecorded && pd.dayWeightRecorded
            ? new Date(
                Number(pd.yearWeightRecorded),
                Number(pd.monthWeightRecorded) - 1,
                Number(pd.dayWeightRecorded)
              )
            : null;
        setFields((prev) => ({
          ...prev,
          height: prev.height || (pd.height != null ? String(pd.height) : ""),
          heightUom: prev.heightUom || pd.heightUOM || "",
          weight: prev.weight || (pd.weight != null ? String(pd.weight) : ""),
          dateOfWeightRecorded: prev.dateOfWeightRecorded || weighed || "",
        }));
      },
      () => {}
    );

    /* The condition chip: read the primary-therapy code, then map it to a name
       through the master list. Via TherapyService, not PatientService's own
       readers — the picker in the header shares its list cache, so the
       patient-agnostic master list is fetched once rather than twice, and the
       id-vs-tag normalising lives in one place. Silent on failure: no therapy
       just means the chip keeps its "Set therapy" affordance. */
    const therapySvc = new TherapyService();
    therapySvc.list(
      (rows) => {
        if (cancelled) return;
        therapySvc.current(
          pid,
          (tag) => {
            if (cancelled || !tag) return;
            const match = (rows || []).find((r) => String(r.tag) === String(tag));
            setTherapyTag(tag);
            if (match) setCondition(match.text);
          },
          () => {}
        );
      },
      () => {}
    );

    /* WEIGHT COMES FROM THE ANTHROPOMETRIC SECTION — the patient's own record —
       rather than from the prescription service's PersonalDetails copy, so a
       weigh-in logged in the section shows up here without being re-entered.
       `current.numericValue` is canonical KILOGRAMS (data.js dualUnit treats it
       as kg and derives lb for display only), which is the unit this field
       wants, so there is no conversion.
       The weigh-in DATE rides along: it is required by the save, and taking it
       from the same reading keeps the two from disagreeing.
       Guarded on `weightTouched` so it never overwrites what the prescriber has
       typed — the PersonalDetails prefill above stays as the fallback for
       patients with no anthropometric reading at all. */
    new AnthropometricService().summary(
      pid,
      (res) => {
        if (cancelled || weightTouched.current) return;
        const params = (res && res.parameters) || [];
        const reading = (name) => {
          const entry = params.find((x) => x && x.parameter === name);
          const cur = entry && entry.current;
          return cur && cur.numericValue != null && isFinite(cur.numericValue) ? cur : null;
        };
        const w = reading("weight");
        const h = reading("height");
        if (!w && !h) return;

        setFields((prev) => {
          const next = { ...prev };
          if (w) {
            next.weight = String(w.numericValue);
            next.dateOfWeightRecorded = w.testDate || prev.dateOfWeightRecorded || "";
          }
          if (h) {
            /* HEIGHT, same source and same rule as weight. It used to come
               only from the prescription service's own PersonalDetails copy,
               which is a different store and is empty for most patients — so a
               patient with a height in Anthropometric data still printed a
               blank Height and no BMI on the prescription.

               `numericValue` is canonical CENTIMETRES here, exactly as it is
               kilograms for weight (anthropometric/data.js dualUnit derives
               ft/in for display only), so the unit code has to be pinned to cm
               alongside it — deriveBmi divides by 100 for anything that is not
               REFLD249, and an empty uom would silently be treated as cm
               anyway. Saying it explicitly is what makes the printed unit and
               the BMI agree. */
            next.height = String(h.numericValue);
            next.heightUom = HEIGHT_UOM_CM;
          }
          return next;
        });
      },
      () => {}
    );

    const seed = () => {
      const groups = {
        complaints: [],
        diagnosis: [],
        medicines: [],
        reports: [],
      };
      const myIds = myActorIds(auth.getUserProfile());

      // Three independent fetches; the cards fill when all have settled. A
      // failure just leaves that one card empty rather than blocking the screen,
      // which is why the error callbacks settle instead of bailing out.
      let outstanding = 4;
      const settle = () => {
        outstanding -= 1;
        if (!outstanding && !cancelled) done(groups);
      };
      const fill = (key, map) => (res) => {
        if (!cancelled) groups[key] = map(res);
        settle();
      };

      ccService.list(pid, fill("complaints", (r) => complaintsToGroups(r, consultDate, myIds)), settle);
      dxService.list(pid, fill("diagnosis", (r) => diagnosisToGroups(r, consultDate, myIds)), settle);

      // Medicines and lab investigations both need the REFLD label tables:
      // medicine v2 stores only the formulation's code (and the prescription
      // API validates on `formulation.label`), and lab investigations store the
      // unit as plain text that has to be matched back to a LAB_TEST_UOM code.
      // A reference-list failure isn't fatal — the rows still load, the
      // medicine save guard names anything it left unresolvable, and a unit
      // falls back to a label-only pair.
      const loadWithTables = (tables) => {
        mdService.list(
          pid,
          fill("medicines", (r) => medicinesToGroups(r, consultDate, myIds, tables)),
          settle
        );
        liService.list(
          pid,
          fill("reports", (r) => labInvestigationsToGroups(r, consultDate, myIds, tables)),
          settle
        );
      };
      refService.getAll(loadWithTables, () => loadWithTables(null));
    };

    seed();

    return () => {
      cancelled = true;
    };
    // employee is derived per render; the ids inside it are stable per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedPatientId, seedAppointmentId, seedConsultDate, rxService, ccService, dxService, mdService, liService, refService]);

  /* MOBILE ONLY — Weight on the Patient details card opens Anthropometric
     data's own Weight sheet (MobileRxAddSheet mounts that editor). Its save is
     the section's own call; this only asks the editor to open. */
  const openWeightSheet = useCallback(
    () => EventService.emit(ANTH_V2_SELECT_PARAM, "weight"),
    []
  );

  /* MOBILE ONLY — after an anthropometric save from that sheet, re-read the
     latest weigh-in so the card and the printed document follow it. The same
     summary read and the same rule as the prefill above (canonical kg / cm,
     the weigh-in date riding with the weight), but NOT guarded on
     weightTouched: a save from the sheet is the prescriber deliberately
     recording a new weight, which is exactly what the field should now show.
     Off the mobile view this effect does nothing. */
  useEffect(() => {
    if (!mobile || !seedPatientId) return undefined;
    let live = true;
    const reread = () =>
      new AnthropometricService().summary(
        seedPatientId,
        (res) => {
          if (!live) return;
          const params = (res && res.parameters) || [];
          const reading = (name) => {
            const entry = params.find((x) => x && x.parameter === name);
            const cur = entry && entry.current;
            return cur && cur.numericValue != null && isFinite(cur.numericValue) ? cur : null;
          };
          const w = reading("weight");
          const h = reading("height");
          if (!w && !h) return;
          setFields((prev) => {
            const next = { ...prev };
            if (w) {
              next.weight = String(w.numericValue);
              next.dateOfWeightRecorded = w.testDate || prev.dateOfWeightRecorded || "";
            }
            if (h) {
              next.height = String(h.numericValue);
              next.heightUom = HEIGHT_UOM_CM;
            }
            return next;
          });
        },
        () => {}
      );
    EventService.addListener(ANTH_V2_REFRESH, reread);
    return () => {
      live = false;
      EventService.removeListener(ANTH_V2_REFRESH, reread);
    };
  }, [mobile, seedPatientId]);

  /* ---------- recover the REFLD code for units the reference list missed ----- */
  /**
   * Runs only for lab-investigation rows whose unit text did not match any
   * LAB_TEST_UOM label, and asks the lab-test synonym map — which stores the
   * REFLD code per sub-test — for the real one. In the common case this fires
   * ZERO requests.
   *
   * `triedUnitsRef` is what stops it looping: a test with no seeded unit stays
   * unresolved forever, so without remembering the attempt every setPickers
   * would queue the same lookups again.
   */
  const triedUnitsRef = useRef({});
  useEffect(() => {
    if (!pickers) return undefined;
    const reports = pickers.find((p) => p.key === "reports");
    if (!reports) return undefined;

    const todo = unresolvedUnitRows(reports.groups).filter(
      (r) => !triedUnitsRef.current[r.sltCode]
    );
    if (!todo.length) return undefined;

    let cancelled = false;
    const codes = todo.map((r) => r.sltCode);
    codes.forEach((c) => {
      triedUnitsRef.current[c] = true;
    });

    const found = {};
    let outstanding = codes.length;
    codes.forEach((code) =>
      ltService.unitFor(code, (hit) => {
        if (hit && hit.unitRef) found[code] = hit;
        outstanding -= 1;
        if (outstanding || cancelled || !Object.keys(found).length) return;
        setPickers((prev) =>
          (prev || []).map((p) =>
            p.key === "reports" ? { ...p, groups: applyUnitRefs(p.groups, found) } : p
          )
        );
      })
    );

    return () => {
      cancelled = true;
    };
  }, [pickers, ltService]);

  /* The touchpoint fetch that used to live here is gone with the strip it fed:
     the header is `compact` on this screen, so past/upcoming appointments are
     not rendered, and a 100-row appointment query on every Rx open bought
     nothing. */

  /* ---------- reason master list: id -> name, fetched once ---------- */
  useEffect(() => {
    let cancelled = false;
    appointmentService.getReasonsForConsultation(
      (res) => {
        if (cancelled) return;
        setReasons(Array.isArray(res) ? res : (res && res.reasons) || []);
      },
      // Decoration — a failure here shouldn't raise a toast the doctor can't act on.
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [appointmentService]);

  const reasonName = (() => {
    const r = reasons.find((x) => x.id === (appointment && appointment.reasonId));
    return (r && (r.name || r.reason)) || "";
  })();

  /* ---------- status changes: same rule as the patient screen ---------- */
  /* THE SHARED FLOW — identical to the dashboard's, so a cancel from Create Rx
     asks for the same reason and leaves refunds to the server, as the expert
     dashboard does (see hooks/useStatusChange). */
  const { changeStatus: runStatusChange, updatingId, dialogs } = useStatusChange({
    /* As on the appointment-details screen: the note the hook opens is about
       this patient, who is already loaded here. */
    patient,
    onApplied: (status) => setAppointment((prev) => ({ ...prev, status })),
    onError: showError,
  });
  const updating = updatingId != null;
  const changeStatus = (status) => runStatusChange(appointment, status);

  /* ---------- builder handlers ---------- */
  const setField = useCallback((field, value) => {
    if (field === "weight") weightTouched.current = true;
    setFields((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Read through a ref so the callback can stay dependency-free — an empty dep
  // list with a direct `lockedPicks` read would capture the pre-seed {} and
  // lock nothing.
  const lockedRef = useRef({});
  lockedRef.current = lockedPicks;

  const togglePick = useCallback(
    (key, id) =>
      setPicks((prev) => {
        // Only the medicine card locks its defaults; the other pickers toggle
        // freely (Nittin, 2026-07-29 — the × and its confirm are medicine-only).
        if (key === "medicines" && ((lockedRef.current[key] || []).indexOf(id) !== -1)) {
          return prev;
        }
        const cur = prev[key] || [];
        const next = cur.indexOf(id) === -1 ? cur.concat(id) : cur.filter((x) => x !== id);
        return { ...prev, [key]: next };
      }),
    []
  );

  /**
   * "+ Add diagnosis" and friends — the record is edited where it lives, in the
   * patient's own section, not duplicated into a prescription-local row. A row
   * that existed only here would vanish on the next reload (every card
   * re-derives from the services) and could already have been published.
   *
   * The builder's state is parked on the way out by the unmount effect above.
   * The section lands in view via `?section=`, and `&add=1` opens its add form
   * once the list has loaded (product, 2026-09-16) — PatientDetails reads and
   * strips the flag; sections/common/useAutoOpenAdd opens it.
   */
  const goAddIn = useCallback(
    (pickerKey) => {
      const target = PATIENT_SECTION_FOR_PICKER[pickerKey];
      if (!target) return;
      /* ON MOBILE THE DETOUR DOES NOT HAPPEN. The section's editor is already
         mounted in a sheet on this screen, so asking it to open is enough —
         and what we emit is that section's OWN add channel, the same constant
         its table's "+ Add" button emits. Nothing new is broadcast, and the
         write path is the form's own, untouched.

         Same OUTCOME as the `&add=1` detour below — the section's add form,
         open — reached without the trip. */
      if (mobile) {
        /* LAB OPENS "DIGITISE REPORT", as the detour does on desktop: `&add=1`
           lands on the patient Lab tab, whose useAutoOpenAdd opens that modal.
           No lab list is mounted here to own one, so DigitiseLauncher does,
           and it needs to be told whose record — hence the payload. */
        const digitise = MOBILE.labAddOpensDigitise && MOBILE.BAR_EVENT_FOR_TAB[target];
        if (digitise) {
          EventService.emit(digitise, { patientId: patient && patient.id, appointmentId });
          return;
        }
        EventService.emit(MOBILE.ADD_EVENT_FOR_TAB[target]);
        return;
      }
      history.push(`/navigo-module/patient/${appointmentId}?section=${target}&add=1`);
    },
    // The map is a per-render literal of constants; the ids inside are fixed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, appointmentId, mobile, patient]
  );

  /* Custom instructions, one entry per box. The card hands over flattened
     lines, never HTML — a multi-line/bulleted box becomes one pointer per line
     in the document, matching how "Other Instructions" is already numbered. */
  /**
   * MOBILE ONLY — the pencil on a Chief complaints / Diagnosis / Medicine row.
   *
   * Editing a record from here is the same trade as adding one: on desktop it
   * means a trip to the patient record, and on a phone that trip IS the
   * interaction. The rail that owns the update form is already mounted on this
   * screen (MobileRxAddSheet), so asking it to open is enough.
   *
   * What is emitted is the section's OWN edit channel — the identical constant
   * its status chip fires on the patient record (MOBILE.EDIT_FOR_PICKER names
   * each one and the payload it expects). Nothing new is broadcast, no form is
   * re-implemented, and the write path is the form's own.
   *
   * The record travels on the row (rxMappers attaches it); a row without one
   * cannot seed a form, so it opens nothing rather than a blank.
   */
  const goEditIn = useCallback((pickerKey, row) => {
    const spec = MOBILE.EDIT_FOR_PICKER[pickerKey];
    if (!spec || !row || !row.record) return;
    EventService.emit(spec.event, spec.payload(row.record));
  }, []);

  const addInstructions = useCallback(
    (entries) => setCustomInstructions((prev) => prev.concat(entries || [])),
    []
  );
  const updateInstruction = useCallback(
    (id, lines) =>
      setCustomInstructions((prev) => prev.map((e) => (e.id === id ? { ...e, lines } : e))),
    []
  );
  const removeInstruction = useCallback(
    (id) => setCustomInstructions((prev) => prev.filter((e) => e.id !== id)),
    []
  );

  const addReferral = useCallback(
    (r) =>
      setReferrals((prev) =>
        prev.concat({
          ...r,
          id: `ref${prev.length + 1}`,
          specialty: r.specialty ? `${r.specialty} consultation` : "Referral",
          // Just added, so it counts — the tick is how it is taken back out.
          selected: true,
        })
      ),
    []
  );

  /* Ticking a referral off DESELECTS it — it leaves the document and the payload
     but stays on the card so it can be ticked back on. It used to be deleted
     outright, so one mis-click destroyed the specialty and reason the clinician had
     typed, with no undo. */
  const toggleReferral = useCallback(
    (id) =>
      setReferrals((prev) =>
        prev.map((r) => (r.id === id ? { ...r, selected: r.selected === false } : r))
      ),
    []
  );

  /* ---------- advised investigations ---------- */

  // The popover commits a BATCH — one row per box ticked in it.
  const addInvestigation = useCallback((labTests) => {
    const batch = Array.isArray(labTests) ? labTests : [labTests];
    setInvestigations((prev) =>
      // The catalogue legitimately lets the same panel be advised twice with
      // different instructions (bridge allows it too), so the id carries a
      // counter rather than being the bare code.
      prev.concat(
        batch.map((labTest, i) => ({
          id: `lt-${labTest.value || labTest.label}-${prev.length + i + 1}`,
          labTest,
          instruction: "",
        }))
      )
    );
  }, []);

  /**
   * Edits an instruction. `key` is a row id when the doctor types, or a LABT
   * code when the catalogue's default advice lands after the row was added —
   * matching both is what lets the async prefill find its row.
   *
   * A code match only fills rows still EMPTY, so advising the same panel twice
   * can't have the second pick's default wipe the first row's typed advice.
   */
  const setInvestigationInstruction = useCallback(
    (key, text) =>
      setInvestigations((prev) =>
        prev.map((r) => {
          if (r.id === key) return { ...r, instruction: text };
          if (r.labTest.value === key && !r.instruction) return { ...r, instruction: text };
          return r;
        })
      ),
    []
  );

  /* ---------- removing a medicine ----------
   *
   * ATOMIC by construction: the API call goes first and NOTHING in the builder
   * changes unless it returns 200. A failure leaves the row exactly where it
   * was, with the reason shown in the dialog — the doctor is never told a
   * medicine left the patient's record when it didn't.
   */
  /** The delete itself, once a FRESH version is in hand. */
  const deleteWithVersion = useCallback(
    (row, version) => {
      mdService.delete(
        patient.id,
        row.medicineId,
        version,
        (res) => {
          if (!mounted.current) return;
          setRemoving(false);

          // Drop the row from all three stores that can hold its id, and drop
          // any group it leaves empty (an empty group renders a "0" caption).
          setPickers((prev) =>
            (prev || []).map((p) =>
              p.key !== "medicines"
                ? p
                : {
                    ...p,
                    groups: p.groups
                      .map((g) => ({ ...g, rows: g.rows.filter((r) => r.id !== row.id) }))
                      .filter((g) => g.rows.length),
                  }
            )
          );
          const drop = (list) => (list || []).filter((x) => x !== row.id);
          setPicks((prev) => ({ ...prev, medicines: drop(prev.medicines) }));
          setLockedPicks((prev) => ({ ...prev, medicines: drop(prev.medicines) }));
          setAutoPicks((prev) => ({ ...prev, medicines: drop(prev.medicines) }));

          setRemovingMedicine(null);
          EventService.emit("showError", {
            message: `${row.label} removed from the prescription and the patient's record`,
            tone: "success",
          });
        },
        (err) => {
          if (!mounted.current) return;
          setRemoving(false);
          const code = err && err.code;
          if (code === 409) {
            // A genuine race: something changed between the version read a
            // moment ago and this delete. Nothing to reconcile — retrying
            // re-reads the version anyway.
            setRemoveError("This patient's medicines just changed. Try again.");
            return;
          }
          if (code === 404) {
            setRemoveError("That medicine is no longer in the patient's record. Reload to refresh the list.");
            return;
          }
          setRemoveError((err && err.message) || "Couldn't remove the medicine — please try again.");
        }
      );
    },
    [mdService, patient]
  );

  const confirmRemoveMedicine = useCallback(() => {
    const row = removingMedicine;
    if (!row || removing) return;

    // Two guards that must never be relaxed. Without a medicineId the URL would
    // read `/undefined`; without a real version the service takes its upsert
    // branch and forks the patient's medicine document while answering 200.
    if (!row.medicineId) {
      setRemoveError("This medicine has no record to remove — it isn't in the patient's Medicines table.");
      return;
    }
    setRemoving(true);
    setRemoveError("");

    // Read the version IMMEDIATELY before deleting rather than trusting one
    // captured at page load. It is the optimistic lock, and anything that
    // touched this patient's medicines since — another tab, another clinician —
    // makes a cached one stale. The service treats a missing/zero version as
    // "upsert" (and there is no unique index on patientId), so a delete sent
    // with 0 would FORK the patient's document and still answer 200 — hence no
    // fallback: no fresh version, no request.
    mdService.list(
      patient.id,
      (doc) => {
        if (!mounted.current) return;
        const version = doc && doc.version;
        if (!version) {
          setRemoving(false);
          setRemoveError("Couldn't read the patient's medicine record. Please try again.");
          return;
        }
        deleteWithVersion(row, version);
      },
      (err) => {
        if (!mounted.current) return;
        setRemoving(false);
        setRemoveError(
          (err && err.message) || "Couldn't reach the patient's medicine record. Please try again."
        );
      }
    );
  }, [removingMedicine, removing, mdService, patient, deleteWithVersion]);
  const removeInvestigation = useCallback(
    (id) => setInvestigations((prev) => prev.filter((r) => r.id !== id)),
    []
  );

  const crumbs = [
    { label: "My appointments", onClick: goToDashboard },
    {
      label: patient ? `${patient.firstName} ${patient.lastName || ""}`.trim() : "Patient",
      onClick: goToPatient,
    },
  ];

  const ready = !loading && !error && patient && appointment;
  const { activeId: activeSection, jumpTo: jumpToSection } = useSectionSpy(
    SECTION_TABS.map((t) => t.id),
    [pickers, patient]
  );
  jumpRef.current = jumpToSection;

  /* ONE consultation date for the whole screen — the document header, the follow-up
     line it prints, and the save body's `consultationDate` + `followUp.date` all read
     this. There used to be two independent `new Date()` calls (one formatted for the
     preview, one raw for the payload), which meant the stored follow-up date could
     disagree with the stored consultation date whenever a save straddled midnight.
     Deliberately NOT memoised on a changing dep: it is the day the prescription is
     written and must not shift mid-edit. */
  const consultationDate = useRef(new Date()).current;

  /**
   * The panes do not scroll until they are PINNED.
   *
   * They are `overflow-y: auto` scrollers, so with the pointer over one of them the
   * wheel drives that pane and the page never moves — the patient header stayed put
   * and the builder scrolled under it, which is backwards. Until a pane reaches the
   * top of the viewport the whole page should move as one; only once the captions
   * are pinned does each pane take its own wheel.
   *
   * Read from the pane's own rect rather than a precomputed scroll offset: it is
   * `position: sticky`, so its top IS the pin state — no threshold to keep in sync
   * with the layout, and it stays correct when the header above changes height.
   */
  const hostRef = useRef(null);
  const [panesPinned, setPanesPinned] = useState(false);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const panes = host.querySelectorAll(".rx-pane");
        if (!panes.length) return;
        // +1 for sub-pixel rounding; sticky parks the pane exactly at the inset.
        const pinned = panes[0].getBoundingClientRect().top <= RX_PANE_INSET + 1;
        setPanesPinned((was) => {
          if (was === pinned) return was;
          // Unpinning with a pane still scrolled would leave it showing its middle
          // once overflow goes hidden. Reaching here normally requires the pane to
          // be at its top already (that is what chains the gesture back to the
          // page), so this is a no-op in the usual path and a guard otherwise.
          if (!pinned) panes.forEach((el) => { el.scrollTop = 0; });
          return pinned;
        });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [ready]);

  const doc = buildDocument({
    patient: fields,
    picks,
    customInstructions: instructionLines(customInstructions),
    referrals: pickedReferrals(referrals),
    followUp,
    pickers,
    investigations,
    prescriber: myDoctor
      ? {
          name: myDoctor.name,
          qualification: myDoctor.speciality || "",
          license: myDoctor.licenseNumber ? `License No. - ${myDoctor.licenseNumber}` : "",
          /* The SAME lookup the save body uses, so the preview cannot promise a
             signature the generated document will not carry, or vice versa.
             null for a doctor without one, and the preview then falls back to
             setting the name in a script face. */
          signature: signatureFor(myDoctor.id),
        }
      : null,
    // The day the prescription is WRITTEN, like bridge — not the appointment's
    // date. An appointment dated months ago (or a rescheduled one) otherwise
    // stamps a signed document with a date long past, and the follow-up line
    // already counts from it, so the two would disagree on the same page.
    // Passed as a Date; buildDocument formats it, so the preview and the payload
    // cannot be looking at two different days.
    consultationDate,
  });

  /* ---------- publish ---------- */
  const pickedFor = (key) =>
    ((pickers || []).find((p) => p.key === key) || { groups: [] }).groups
      .reduce((all, g) => all.concat(g.rows), [])
      .filter((r) => (picks[key] || []).indexOf(r.id) !== -1);

  const publishPrescription = (notify) => {
    if (saving) return;
    // Bridge guards on `doctorDetails === null`, which never fires for a fresh
    // Rx (the key is absent, so it's undefined) — publish then goes out with no
    // doctor. Truthiness instead.
    if (!myDoctor) {
      logRxEvent(rxService, rxSession.current, "ERROR_PRESCRIPTION_DOCTOR");
      showError("Your account isn't registered as a doctor, so it can't issue a prescription.");
      return;
    }
    // The prescription API rejects a medicine with no formulation or no time
    // period (validateMedicines), and phr's medicine records don't always carry
    // them — a free-text medicine has no formulation at all. Catch it here so
    // the message names the medicine and where to fix it.
    const picked = {
      diagnosis: pickedFor("diagnosis"),
      complaints: pickedFor("complaints"),
      reports: pickedFor("reports"),
      medicines: pickedFor("medicines"),
      // Already payload-shaped — this card builds rows rather than picking
      // them. buildSaveBody still applies the wire transform (upper-cased
      // label + labName), like it does for instructions.
      investigations: investigations.map((r) => ({ save: r })),
    };
    const gaps = medicineGaps(picked.medicines);
    if (gaps.length) {
      showError(
        `${gaps
          .map((g) => `${g.label} has ${g.gap}`)
          .join("; ")}. Complete it in the Medicines section, or untick it here.`
      );
      return;
    }
    setSaving(true);
    const body = buildSaveBody({
      patientId: patient.id,
      // The consultation this Rx belongs to. Required server-side to raise the
      // "Order medicine" floater: without it api-prescription-service can't resolve the
      // appointment (which supplies the plan and the self-vs-family check), so the
      // floater is never created and the patient can't order the medicines.
      appointmentId,
      patient,
      fields,
      picked,
      // The document's numbered advice is what gets prescribed as instructions.
      instructions: doc.otherInstructions,
      // Referrals now have a home on the wire (api-prescription-service's
      // optional `referrals`), so what the card captures reaches the PDF
      // instead of living only in the preview. Only the TICKED ones — the same
      // set the preview shows.
      referrals: pickedReferrals(referrals),
      // Stored structured as well as in the instruction line above. The line keeps
      // the printed document self-contained; this makes "follow-ups due next week"
      // an actual query instead of a regex over an uppercase sentence.
      followUp,
      doctor: myDoctor,
      // The SAME Date object the preview used, so `followUp.date` counts from
      // exactly this day.
      consultationDate,
      employee,
    });
    rxService.save(
      body,
      { ...notify, phone: patient.phoneno, clientCode: patient.clientCode },
      (res) => {
        // The saved prescription IS the record now — drop the local scratch so
        // leaving the screen can't park these edits and replay them over it.
        // BEFORE the mounted check: a doctor who left while this was in flight
        // has already parked the scratch on unmount, and it must not survive
        // the save it belongs to.
        keepScratch.current = false;
        {
          const scratchKey = scratchKeyRef.current || appointmentId;
          /* Still on the screen: whatever is parked was restored into it — clear.
             Left mid-save: clear only OUR copy; a later visit to this Rx may have
             parked its own work under the same key since. */
          const parked = mounted.current ? null : readScratch(scratchKey);
          if (!parked || !parked.owner || parked.owner === scratchOwner.current) clearScratch(scratchKey);
        }
        // The legacy screen's publish events — before the mounted check too:
        // they belong to the publish, not to the screen.
        rxPublished(rxService, rxSession.current, patient);
        if (!mounted.current) return;
        setSaving(false);
        setNotifyOpen(false);
        EventService.emit("showError", {
          message: "Prescription published",
          tone: "success",
        });
      },
      (err) => {
        if (!mounted.current) return;
        setSaving(false);
        /* "Unauthorized Access" is the ONLY thing this endpoint says when the
           signed-in doctor lacks prescribing rights, and it is a 401 dressing a
           403: api-prescription-service server/check_authorization.go:23-40
           runs HasPrescriptionPermission for every `doctor_web` client and
           401s when the account holds neither role 1 nor PRESCRIPTION_ROLE_ID.
           The session is fine — re-logging in changes nothing — so repeating
           the server's word here sends the doctor to fix the wrong thing. */
        const raw = (err && err.message) || "";
        showError(
          /unauthori[sz]ed/i.test(raw)
            ? "Your doctor account isn't permitted to issue prescriptions. " +
              "This isn't a login problem — ask an admin to grant the account " +
              "prescribing rights."
            : `Couldn't publish the prescription: ${raw || "please try again."}`
        );
      }
    );
  };


  /* ---------------------------------------------------------------------
     Pulled out of the return so BOTH layouts render the same tree.

     The mobile screen is this host inside a different frame — pinned rows
     above it, an action bar below — not a second copy of it. Reproducing the
     card stack there would have left two lists to keep in step, and the next
     card added here would have been missing on a phone. Desktop's output is
     unchanged: same JSX, same order, one indent shallower.
     --------------------------------------------------------------------- */
  const rxHost = (
    <div className={`rx-host${panesPinned ? " is-pinned" : ""}`} ref={hostRef}>
      {/* ---- left: the builder ---- */}
      <section className="rx-pane" aria-label="Review prescription">
        {/* Caption + jump tabs pin to the top of the pane while the cards
            scroll under them — the tabs are navigation for this list, and
            they were scrolling out of reach exactly when they were useful. */}
        <div className="rx-pane-head">
        <header className="rx-cap">
          <button
            type="button"
            className="rx-cap-back"
            aria-label="Back to patient"
            onClick={goToPatient}
          >
            &#8249;
          </button>
          <h2 className="rx-cap-title">Review prescription</h2>
        </header>

        <RxSectionTabs
          tabs={SECTION_TABS}
          activeId={activeSection}
          onJump={jumpToSection}
        />
        </div>

        <div className="rx-cards">
          {/* Prescribing doctor — always the signed-in doctor. A
              prescription can't be written on someone else's behalf, so
              there is no picker. */}
          <div className="rx-card">
            <div className="rx-card-head">
              <h3 className="rx-card-title">Prescribing doctor</h3>
            </div>
            {myDoctor ? (
              <div className="rx-prescriber">
                <span className="rx-prescriber-name">{myDoctor.name}</span>
                <span className="rx-prescriber-meta">
                  {[
                    myDoctor.speciality,
                    myDoctor.licenseNumber ? `License No. - ${myDoctor.licenseNumber}` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            ) : (
              <p className="rx-note is-error">
                Your account isn&rsquo;t registered as a doctor, so it can&rsquo;t
                issue a prescription. A prescription is always signed by the
                doctor writing it.
              </p>
            )}
          </div>

          <div id="rx-sec-patient">
            <PatientDetailsCard values={fields} onChange={setField} onUpdate={openDetails} onWeightClick={mobile && MOBILE.rxWeightSheet ? openWeightSheet : undefined} />
          </div>

          {rxLoading && <p className="rx-note">Loading this patient&rsquo;s records…</p>}

          {(pickers || PICKERS).map((p) => (
            <div id={TAB_FOR_PICKER[p.key]} key={p.key}>
            <RxPickerCard
              title={p.title}
              /* ZY-10393: Lab investigations starts collapsed. It lists every earlier
                 result on the record, which pushed the cards below it off screen; its
                 header still shows how many are ticked. Every other card starts open. */
              defaultOpen={p.key !== "reports"}
              groups={p.groups}
              selected={picks[p.key] || []}
              onToggle={(id) => togglePick(p.key, id)}
              locked={p.key === "medicines" ? lockedPicks.medicines || [] : []}
              // Only rows backed by a medicine v2 record can be deleted.
              // A draft-resumed row has no medicineId, so it gets no ×
              // rather than a button that would 400. Nor does an "Updated
              // today" row (Nittin, 2026-10-07): that medicine was on the
              // record before this consultation, and × soft-deletes it from
              // the record — it stays locked in, with no way to delete it here.
              removable={
                p.key === "medicines"
                  ? p.groups
                      .reduce((all, g) => all.concat(g.rows), [])
                      .filter(
                        (r) =>
                          r.medicineId &&
                          !(r.isUpdated && !r.isNew) &&
                          (lockedPicks.medicines || []).indexOf(r.id) !== -1
                      )
                      .map((r) => r.id)
                  : []
              }
              onRemove={
                p.key === "medicines"
                  ? (row) => {
                      setRemoveError("");
                      setRemovingMedicine(row);
                    }
                  : undefined
              }
              /* "+ Add complaint" wraps to a second line at 360px and
                 pushes the card head out of shape; the card title above
                 already says which list it adds to. */
              /* MOBILE ONLY, and only once the real records are in: until the
                 seed lands, `pickers` is null and the card renders the static
                 PICKERS fallback, whose rows carry no record — a pencil there
                 would be a control that does nothing. */
              onEdit={
                mobile && MOBILE.rxRowEdit && pickers && MOBILE.EDIT_FOR_PICKER[p.key]
                  ? (row) => goEditIn(p.key, row)
                  : undefined
              }
              addLabel={mobile ? "Add" : ADD_LABEL_FOR_PICKER[p.key]}
              onAdd={
                PATIENT_SECTION_FOR_PICKER[p.key]
                  ? () => goAddIn(p.key)
                  : undefined
              }
            />
            </div>
          ))}

          {/* Advised investigations is NOT a picker — it creates what
              is being ordered rather than choosing from the record, so
              it sits outside the .map above. */}
          <div id="rx-sec-investigations">
            <AdvisedInvestigationsCard
              rows={investigations}
              onAdd={addInvestigation}
              onChangeInstruction={setInvestigationInstruction}
              onRemove={removeInvestigation}
            />
          </div>

          <div id="rx-sec-instruction">
            <CustomInstructionCard
              instructions={customInstructions}
              onAdd={addInstructions}
              onUpdate={updateInstruction}
              onRemove={removeInstruction}
              drafts={restoredInstructionDrafts}
              onPendingChange={onInstructionsPending}
              onDraftsChange={onInstructionDrafts}
            />
          </div>
          <div id="rx-sec-referrals">
            {/* The card shows EVERY referral, ticked or not. Only the ticked
                ones reach the document and the payload — see pickedReferrals. */}
            <ReferralsCard
              referrals={referrals}
              onAdd={addReferral}
              onToggle={toggleReferral}
            />
          </div>
          <div id="rx-sec-followup">
            <FollowUpCard value={followUp} onChange={setFollowUp} />
          </div>

        </div>
      </section>

      {/* ---- right: the live document ---- */}
      <section className="rx-pane" aria-label="Document preview">
        <div className="rx-pane-head">
          <header className="rx-cap">
            <h2 className="rx-cap-title">Document preview</h2>
          </header>
        </div>
        <PrescriptionDocument doc={doc} />
      </section>
    </div>
  );

  /* The portalled overlays, likewise shared: the patient-details dialog, the
     remove-medicine confirm, the cancel/refund dialogs from useStatusChange,
     and the publish dialog. They stay OUTSIDE rxHost for the reason given
     where detailsOpen is declared — the panes are sticky, and a stacking
     context there would trap a fixed scrim. The mobile screen renders these
     outside its host too. */
  const rxOverlays = (
    <>
            {detailsOpen && (
              <PatientDetailsDialog
                patient={patient}
                onClose={() => setDetailsOpen(false)}
                onSaved={(updated) => {
                  setDetailsOpen(false);
                  refetchPatient(updated);
                }}
              />
            )}

            {removingMedicine && (
              <RemoveMedicineDialog
                medicineName={removingMedicine.label}
                busy={removing}
                error={removeError}
                onConfirm={confirmRemoveMedicine}
                onClose={() => {
                  setRemovingMedicine(null);
                  setRemoveError("");
                }}
              />
            )}

            {/* The shared cancel dialog — same sequence as the dashboard. */}
      {dialogs}

      {notifyOpen && (
              <PublishDialog
                patient={patient}
                saving={saving}
                onClose={() => setNotifyOpen(false)}
                onPublish={publishPrescription}
              />
            )}
    </>
  );

  /* The mobile arrangement of everything above. After the last hook, so hook
     order is identical on both paths; the desktop tree below is unreachable on
     mobile and unchanged by it. */
  if (mobile) {
    return (
      <MobilePrescriptionScreen
        host={rxHost}
        overlays={rxOverlays}
        loading={loading}
        error={error}
        ready={ready}
        appointment={appointment}
        updating={updating}
        onChangeStatus={changeStatus}
        onBack={goToPatient}
        onBackToDashboard={goToDashboard}
        saving={saving}
        patient={patient}
        appointmentId={appointmentId}
        /* requestPublish — the handler the desktop header's primary action
           uses. It refuses while a custom instruction is typed but not added,
           or over 500 characters, and otherwise opens the notify-channel
           dialog. Not publishPrescription, which skips that dialog and
           publishes with `notify` undefined. */
        onPublish={requestPublish}
        instructionsPending={instructionsPending}
        /* The Patient details dialog in rxOverlays takes no scroll lock; the
           mobile screen holds one while it is up. */
        patientDetailsOpen={detailsOpen}
      />
    );
  }

  return (
    <div className="patient-page">
      <div className="patient-scroll">
        <TopBar crumbs={crumbs} onBack={goToPatient} />

        {loading && <div className="patient-message">Loading…</div>}

        {!loading && error && (
          <div className="patient-message is-error">
            <p>{error}</p>
            <button type="button" className="ph-btn ph-btn-primary" onClick={goToDashboard}>
              Back to appointments
            </button>
          </div>
        )}

        {ready && (
          <>
            <PatientHeaderCard
              patient={patient}
              languages={languages}
              languagesState={languagesState}
              onLanguagesSaved={refetchLanguages}
              onPatientUpdated={refetchPatient}
              onEditDetails={openDetails}
              appointment={appointment}
              originalBookingName={originalBookingName}
              reasonName={reasonName}
              /* Identity band only. No appointment strip, no touchpoints —
                 this screen prescribes FOR that appointment, so restating it
                 here is noise. */
              compact
              condition={condition}
              therapyTag={therapyTag}
              onTherapyChanged={(tag, text) => {
                setTherapyTag(tag);
                setCondition(text);
              }}
              onOpenAppointment={(id) => history.push(`/navigo-module/patient/${id}`)}
              updating={updating}
              onChangeStatus={changeStatus}
              primaryLabel={saving ? "Publishing…" : "Publish Rx"}
              // Publishing opens the "send to patient via…" dialog first, like
              // bridge — the switches are what the backend fans out.
              onPrimaryAction={saving ? undefined : requestPublish}
            />

            {rxHost}

            {rxOverlays}
          </>
        )}
      </div>
    </div>
  );
};

ReviewPrescription.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default ReviewPrescription;
